import type { Pinia } from 'pinia';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { getCaller } from '@common/libs/caller';
import type { Caller } from '@common/libs/caller';

/**
 * Enrichit chaque store Pinia.
 *
 * Évite d'importer le routeur ou un client HTTP dans chaque store : ils sont
 * disponibles via `this.$caller('api')`, `this.$router`, `this.apiBaseUrl`.
 *
 * Appelle aussi `setup()` une seule fois sur les stores qui en définissent un,
 * ce qui donne un point d'initialisation clair (restauration du jeton, abonnements)
 * sans avoir à l'orchestrer depuis les vues.
 */
export default {
	install(
		app: App,
		{ pinia, router, apiBaseUrl }: { pinia: Pinia; router: Router; apiBaseUrl: string },
	) {
		pinia.use(({ store }) => {
			store.$app = app;
			store.$router = router;
			store.apiBaseUrl = apiBaseUrl;
			store.$caller = (name = 'api') => getCaller(name, pinia);
			store.$initialState = JSON.parse(JSON.stringify(store.$state));
			store.$reset = () => {
				store.$patch(JSON.parse(JSON.stringify(store.$initialState)));
			};

			if (typeof store.setup === 'function' && !store.__isSetup) {
				store.__isSetup = true;
				try {
					Promise.resolve(store.setup()).catch(console.error);
				} catch (error) {
					console.error(error);
				}
			}
		});
	},
};

declare module 'pinia' {
	export interface PiniaCustomProperties {
		$app: App;
		$router: Router;
		$caller: (name?: string) => Caller;
		$initialState: Record<string, unknown>;
	}
}
